/**
 * A module that bootstraps the A/B testing framework.
 * @module abt-core/app
 */

import _ from 'lodash'
import clientData from './client-data'
import debugMode from '../abt-utils/debug-mode'
import eventManager from 'eventmanager'
import * as abtestRunner from './abtest-runner'
import * as dependencies from './dependencies'
import * as qa from './qa'
import * as abtestModules from './abtest-modules'
import { error as logError } from '../abt-utils/logger'
import './polyfill'

/**
 * Initializes the A/B testing framework.
 *
 * Ensures the application is loaded and executed in the correct order.
 *
 * @returns {array} Returns an array of promises that resolves when all A/B tests have completed.
 */
export function init () {
  debugMode.init()
  dependencies.init()
  clientData.init()

  var tests = abtestModules.get()
  var promises = _initTests(tests)
  abtestModules.expose(tests)

  _initAbtMenu()

  return promises
}

/**
 * Initializes the A/B tests.
 *
 * If a test's critiera is not met, undefined will be returned or resolved, which will cause this
 * individual test to not run. Any errors in the test execution lifecycle will bubble up to the
 * catch block of the promise chain and get handled by the logger.
 *
 * @param {array} tests - The array of resolved A/B tests.
 * @returns {array} Returns an array of promises that resolves when all A/B tests have completed.
 */
function _initTests (tests) {
  var metricsDataSet = [_getMonetateMetricsData()]

  var promises = _.map(tests, function (abtest) {
    var promise = Promise.resolve(abtest)
      .then(_guard(abtestRunner.criteria))
      .then(_guard(abtestRunner.customCriteria))
      .then(_guard(abtestRunner.createCookie))
      .then(_guard(abtestRunner.setMetricsData))
      .catch(logError)

    metricsDataSet.push(promise)

    return promise
      .then(_guard(abtestRunner.runTestRecipe))
      .then(_guard(abtestRunner.appendLids))
      .catch(logError)
  })

  Promise.all(metricsDataSet)
    .then(_abtestEvarsReady)
    .catch(_abtestEvarsReady)

  Promise.all(promises)
    .then(_abtestReady)
    .catch(_abtestReady)

  return promises
}

/**
 * Gets a promise representing that Monetate metrics data has been set.
 *
 * Resolves with the following criteria:
 *   1. window.MT_flag has already been set
 *   2. monetate:mt_flag:ready event triggered
 *   3. 500ms timeout is fired
 *
 * @returns {promise} Returns the Monetate metrics data promise.
 */
function _getMonetateMetricsData () {
  return new Promise(function (resolve, reject) {
    if (window.MT_flag) return resolve()

    var monetateTimeout = window.setTimeout(function () {
      return resolve()
    }, 500)

    eventManager.when('monetate:mt_flag:ready', function () {
      window.clearTimeout(monetateTimeout)
      return resolve()
    })
  })
}

/**
 * Gaurds an action from being applied to the test config.
 * @param {function} action - The action to apply.
 * @returns {object|undefined} Returns the result of the applied action, which should be the test
 * config, otherwise returns undefined.
 */
function _guard (action) {
  return function (test) {
    if (!test) return undefined
    return action(test)
  }
}

/** Triggers abtest:evars:ready event so metrics data can be sent. */
function _abtestEvarsReady () {
  eventManager.trigger('abtest:evars:ready')
}

/** Triggers abtest:ready event when our framework has finished running. */
function _abtestReady () {
  eventManager.trigger('abtest:ready')
}

/** Checks if menu is turned on and loads the script. */
function _initAbtMenu () {
  qa.enabled() && eval(window.localStorage.abtestMenu) // eslint-disable-line no-eval
}



/*****************
 ** WEBPACK FOOTER
 ** ./src/abt-core/app.js
 ** module id = 21
 ** module chunks = 2
 **/